// practiceDay.js - "today" as the PRACTICE's calendar day (pure; no React).
//
// Round 5 #7: many writes took "today" from new Date().toISOString().slice(0, 10),
// the UTC day. After 8pm Eastern (7pm in winter) that is already tomorrow, so
// a stopped medication, a dose or a filed document carried the next date.
// The practice day is read in practices.timezone (the zone the schedule,
// waitlist offers and Insights snapshots already use), falling back to
// Eastern (every current practice is in North Carolina).
//
// React screens use src/hooks/usePracticeToday.js; lib helpers that receive
// the acting profile (`me`) call practiceTodayOf(me) directly.
// scripts/check-utc-today.mjs keeps new UTC "today" expressions out of src.
import { DEFAULT_TZ, todayYmd } from "./waitlistCore.js";

export { DEFAULT_TZ, todayYmd };

// The practice's IANA zone from a profile row (profiles + practices(timezone)).
export function practiceTzOf(profile) {
  const tz = profile && profile.practices && profile.practices.timezone;
  if (typeof tz !== "string" || !tz) return DEFAULT_TZ;
  // A mistyped zone would make Intl throw on every call; Eastern is the safe
  // default for NC practices.
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return tz; } catch (e) { return DEFAULT_TZ; }
}

// YYYY-MM-DD of `now` in the profile's practice zone.
export function practiceTodayOf(profile, now = new Date()) {
  return todayYmd(practiceTzOf(profile), now);
}

// "YYYY-MM-DD" plus n calendar days (negative = past), with no time-zone
// shift: the arithmetic runs on a UTC date that only carries the calendar day.
export function addDaysToYmd(ymd, n) {
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.getUTCFullYear() + "-" + String(t.getUTCMonth() + 1).padStart(2, "0") + "-" + String(t.getUTCDate()).padStart(2, "0");
}

// Round 6 #9: the practice day `n` days before today, for "last n days"
// ranges. new Date(Date.now() - n * 864e5).toISOString().slice(0, 10) (or
// d.setDate(d.getDate() - n) then the same) is the UTC day, one day late
// every evening after 8pm Eastern; scripts/check-utc-today.mjs counts both.
export function practiceDaysAgo(tz, n, now = new Date()) {
  return addDaysToYmd(todayYmd(tz || DEFAULT_TZ, now), -n);
}

// The practice-zone calendar day of a timestamp (YYYY-MM-DD), for showing a
// *_at column as a date. ts.slice(0, 10) is its UTC date: a row written at
// 9pm Eastern would show tomorrow. Empty or unparseable input returns "".
export function timestampYmd(ts, tz) {
  if (!ts) return "";
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return "";
  return todayYmd(tz || DEFAULT_TZ, d);
}
